import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import all from "ramda/src/all";
import any from "ramda/src/any";
import propEq from "ramda/src/propEq";
import filter from "ramda/src/filter";
import compose from "ramda/src/compose";
import cond from "ramda/src/cond";
import always from "ramda/src/always";
import T from "ramda/src/T";
import { styled } from "@mui/material/styles";

import { Grid } from "../../UI/Grid";
import { Box } from "../../UI/Box";
import { Table as MuiTable } from "../../UI/Table";
import { useNetwork } from "../../hooks/useNetwork";
import { PlacementWorksheetTableHead } from "./PlacementWorksheetTableHead";
import { PlacementWorksheetTableBody } from "./PlacementWorksheetTableBody";
import { Switch } from "../Switch";
import { normalize } from "../../helpers/normalize";
import { usePlacementWorksheetTableContext } from "./PlacementWorksheetTableProvider";
import {
  usePlacementWorksheetContext,
  usePlacementWorksheetSettingsContext,
} from "../../contexts/PlacementWorksheetContext";
import { IPlacementWorksheetUnit } from "../../services/PlacementWorksheetApi";
import { SearchInput } from "./SearchInput";
import { occupantFilterPredicate } from "../PlacementWorksheetRightPopup/PlacementWorksheetUplacedGroupsSection";
import { PSTableRowSelectBottomPopup } from "./PSTableRowSelectBottomPopup";
import { ModalProvider } from "../../contexts/ModalContext";
import { UploadUnitNumbersButton } from "./UploadUnitNumbersButton";
import { ALL_UNIT_OPTION, PlacementWorksheetTableFilterSelect } from "./PlacementWorksheetTableFilterSelect";
import { pathOrArray } from "../../helpers/pathOrArray";

// Create a styled table that supports sticky headers and columns
const Table = styled(MuiTable)`
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;
  width: auto;
  min-width: 100%;

  /* Ensure proper overflow behavior */
  .MuiTableCell-root {
    overflow: visible;
  }

  /* Style for sticky columns to prevent collapsing */
  .sticky-column {
    white-space: normal !important;
    overflow: visible !important;
  }
`;

const isIsAllUnitsWithoutNumber = all(propEq("unitNumber", null));

const getNotAssignedRows = filter(compose(any(propEq("roomLabelId", null)), pathOrArray(["occupants"])));

export const PlacementWorksheetTable = () => {
  const { selectedNetwork, getL2 } = useNetwork();
  const { occupants, l2Id, l3Id, fetchPlacementWorksheet, isLoading, allowedCount, matchGroupAtLimit } = usePlacementWorksheetContext();
  const [subnetworkInfo, setSubnetworkInfo] = React.useState<any>(null);

  // Create a ref for the table container
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const { labels } = usePlacementWorksheetSettingsContext();
  const { filterUnit, setFilterUnit } = usePlacementWorksheetTableContext();
  const { isShowAll, setIsShowAll, search } = usePlacementWorksheetTableContext();
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (l2Id && selectedNetwork) {
      const l2inf = getL2(selectedNetwork.l1Id, l2Id);
      setSubnetworkInfo(l2inf);
    }
  }, [l2Id, selectedNetwork]);


  const isReadOnly = React.useMemo(() => {
    if (!subnetworkInfo) return false;
    return !!(subnetworkInfo.matchingRoundStatus === "ARCHIVED");
  }, [subnetworkInfo]);
  useEffect(() => {
    if (selectedRows.length > 0) {
      setIsOpen(true);
    } else {
      setIsOpen(false);
    }
  }, [selectedRows]);

  // reset selected rows
  useEffect(() => {
    setSelectedRows([]);
  }, [l3Id]);



  const filterUnits = useCallback(
    filter<IPlacementWorksheetUnit>((unit) => (search ? unit.occupants.some(occupantFilterPredicate(search)) : true)),
    [search]
  );


  const filterUnitsByResidents = useCallback(
    filter<IPlacementWorksheetUnit>(
      cond([
        [always(filterUnit.value === "filled"), (unit: any) =>
          unit.occupants && (labels?.length || 0) > 0 ? unit.occupants.length === (labels?.length || 0) : false
        ],
        [always(filterUnit.value === "unfilled_partial"), (unit: any) =>
          unit.occupants && (labels?.length || 0) > 0 ? unit.occupants.length < (labels?.length || 0) : true
        ],
        [T, always(true)],
      ])
    ),
    [labels?.length, filterUnit]
  );

  const filteredData = useMemo(() => {
    if (!occupants || !occupants.assignedUnits || isLoading) {
      return [];
    }

    // if (allowedCount < occupants.assignedUnits.length) {
    //   return occupants.assignedUnits.filter(({ occupants }) => occupants.length);
    // }

    const assignedUnits = filterUnitsByResidents(occupants.assignedUnits);

    return filterUnits(assignedUnits);
  }, [normalize(occupants?.assignedUnits), allowedCount, filterUnit.value, filterUnits, isLoading]);


  // Scroll to table when data is loaded after filter selection
  useEffect(() => {
    // Check if data is loaded and not in loading state
    // We specifically want to trigger this after loading has completed

    const tableBody = document.querySelector('.MuiTableBody-root');
    const tableRows = tableBody ? tableBody.querySelectorAll('tr') : [];
    if (filteredData && filteredData.length > 0 && !isLoading && tableRows.length > 0) {
      // Find the element to scroll to
      // Add a longer delay to ensure the table body has fully rendered
      setTimeout(() => {
          const tableContainer = document.getElementById('placement-worksheet-table-container');
          if (tableContainer) {
            // Scroll to the element
            tableContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
      }, 0); // 800ms delay to ensure table body has rendered
    }
  }, [filteredData, isLoading, l3Id]);

  const sortedUnits = useMemo(() => {
    return filteredData.sort(function(a, b) {
      if ((a.unitNumber === null ||  a.unitNumber === "") && (b.unitNumber === null || b.unitNumber === "")) {
        return 0;
      }
      else if (a.unitNumber === null || a.unitNumber === "") {
        return 1;
      }
      else if (b.unitNumber === null || b.unitNumber === "") {
        return -1;
      }

      return a.unitNumber.localeCompare(b.unitNumber, undefined, {
        numeric: true,
        sensitivity: 'base'
      });
    });
  }, [filteredData]);

  const handleRowSelect = useCallback(
    (id: number) => {
      const index = selectedRows.findIndex((item) => item === id);
      if (index === -1) {
        setSelectedRows(selectedRows.concat(id));
      } else {
        setSelectedRows([...selectedRows.slice(0, index), ...selectedRows.slice(index + 1)]);
      }
    },
    [selectedRows]
  );

  const handleSelectAll = useCallback(
    (checked: boolean) => {
      setSelectedRows(checked ? filteredData.map(({ unitId }) => unitId) : []);
    },
    [selectedRows, filteredData]
  );

  const isAllChecked = useMemo(() => {
    return filteredData.length > 0 && selectedRows.length === filteredData.length;
  }, [selectedRows, filteredData]);

  const isIndeterminate = useMemo(() => {
    return selectedRows.length > 0 && selectedRows.length < filteredData.length;
  }, [selectedRows, filteredData]);

  const handleSwitchLifestyleQuestionsChange = (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setIsShowAll(checked);
  };

  const handleCloseBottomPopup = () => {
    setIsOpen(false);
  };

  const refetchData = useCallback(async () => {
    try {
      await fetchPlacementWorksheet();
      // reset all state
      setSelectedRows([]);
      setFilterUnit(ALL_UNIT_OPTION);
      setIsShowAll(false);
    } catch (error) {
      throw error;
    }
  }, [fetchPlacementWorksheet]);

  const handleUnselect = () => setSelectedRows([]);

  const notAssignedRows = getNotAssignedRows(filteredData as any);

  return (
    <>
      <Grid container spacing={0} mb={isOpen ? "15vh" : 0}>
        <Grid item container gap={4} sx={{ marginBottom: '10px', paddingBottom: 0, position: 'relative', zIndex: 15 }}>

          {isIsAllUnitsWithoutNumber(filteredData) && !isLoading && filteredData.length && (
            <Grid item>
              <ModalProvider>
                <UploadUnitNumbersButton refetchData={refetchData} filteredData={filteredData} />
              </ModalProvider>
            </Grid>
          )}
          <Grid item md />
          <Grid item>
            <PlacementWorksheetTableFilterSelect />
          </Grid>
          <Grid item>
            <Switch
              label="Show Lifestyle Questions"
              switchProps={{
                checked: isShowAll,
                onChange: handleSwitchLifestyleQuestionsChange,
              }}
              isDisabled={isReadOnly}
            />
          </Grid>
          <Grid item>
            <SearchInput />
          </Grid>
        </Grid>
        <Grid item xs={12} sx={{ height: '60vh', display: 'flex', flexDirection: 'column', position: 'relative', marginTop: 0, paddingTop: 0 }} ref={tableContainerRef} id="placement-worksheet-table-container">
          <Box className="table-scroll-container" sx={{
            overflowX: "scroll", /* Always show horizontal scrollbar */
            overflowY: "auto",
            flex: 1,
            maxHeight: "100%",
            position: "relative",
            zIndex: 5,
            paddingRight: 0,
          }} pb={4} pr={2}>
          <Table padding="checkbox" sx={{ marginTop: 0 }}>
            <PlacementWorksheetTableHead
              isAllChecked={isAllChecked || isIndeterminate}
              isIndeterminate={isIndeterminate}
              onSelectAll={handleSelectAll}
              // @ts-ignore
              notAssignedRowsQty={notAssignedRows.length}
              isReadonly={isReadOnly}
            />
            <PlacementWorksheetTableBody
              rows={sortedUnits}
              isLoading={isLoading}
              selectedRows={selectedRows}
              onRowSelect={handleRowSelect}
              isReadOnly={isReadOnly}
              matchGroupAtLimit={matchGroupAtLimit}
            />
          </Table>
          </Box>
        </Grid>
      </Grid>
      <ModalProvider>
        <PSTableRowSelectBottomPopup
          isOpen={isOpen}
          onSuccess={refetchData}
          selectedRows={selectedRows}
          clearSelection={handleUnselect}
          onClose={handleCloseBottomPopup}
        />
      </ModalProvider>
    </>
  );
};
